import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { 
  ArrowRight, Instagram, Youtube, MessageCircle, Users, Zap, Shield,
  CheckCircle, TrendingUp, Eye, Heart, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://businessmintpr.com';

// Schema.org structured data for Social Media Service
const socialMediaSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Social Media Marketing Services",
  "description": "Get your brand promoted by verified influencers on Instagram, YouTube, and Telegram with guaranteed views and authentic engagement.",
  "provider": {
    "@type": "Organization",
    "name": "BusinessMint PR",
    "url": SITE_URL
  },
  "serviceType": "Social Media Marketing",
  "areaServed": {
    "@type": "Country",
    "name": "India"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Social Media Packages",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Instagram Promotion",
          "description": "Feed posts, Stories, and Reels on verified influencer accounts"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "YouTube Promotion",
          "description": "Video mentions and dedicated reviews on popular channels"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Telegram Promotion",
          "description": "Posts in high-engagement community channels"
        }
      }
    ]
  }
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": SITE_URL
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Services",
      "item": `${SITE_URL}/marketplace`
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Social Media",
      "item": `${SITE_URL}/services/social-media`
    }
  ]
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What social media platforms do you cover?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We offer promotions on Instagram (feed posts, stories, reels), YouTube (videos, shorts, mentions), Telegram channels, and other major platforms. Each platform has verified accounts with real engagement."
      }
    },
    {
      "@type": "Question",
      "name": "How do you verify influencer accounts?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We verify all accounts for authentic engagement, follower quality, and brand safety. We check engagement rates, follower demographics, and content history before adding any account to our network."
      }
    },
    {
      "@type": "Question",
      "name": "What content formats are available?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Depending on the platform, we offer feed posts, stories, reels, video mentions, dedicated videos, shorts, and channel posts. Each listing clearly shows what format is included."
      }
    },
    {
      "@type": "Question",
      "name": "Do I get analytics after posting?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! You receive detailed analytics including views, likes, comments, and engagement metrics. We provide screenshots and links as proof of posting."
      }
    }
  ]
};

const SocialMedia = () => {
  const [socialPages, setSocialPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetchSocialPages();
  }, []);

  const fetchSocialPages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-pages`);
      const data = await res.json();
      setSocialPages(data.slice(0, 6));
    } catch (err) {
      console.error('Error fetching social pages:', err);
    } finally {
      setLoading(false);
    }
  };

  const platforms = [
    {
      icon: Instagram,
      name: 'Instagram',
      description: 'Feed posts, Stories, Reels on verified influencer accounts',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Youtube,
      name: 'YouTube',
      description: 'Video mentions, dedicated reviews, shorts on popular channels',
      color: 'from-red-500 to-red-600'
    },
    {
      icon: MessageCircle,
      name: 'Telegram',
      description: 'Posts in high-engagement community channels',
      color: 'from-blue-400 to-blue-500'
    }
  ];

  const features = [
    {
      icon: Users,
      title: 'Verified Creators',
      description: 'All influencers and pages are verified with real engagement metrics.'
    },
    {
      icon: Eye,
      title: 'Guaranteed Views',
      description: 'Minimum view guarantees on every post. Get real exposure.'
    },
    {
      icon: Zap,
      title: 'Fast Turnaround',
      description: 'Most posts go live within 24-48 hours of content approval.'
    },
    {
      icon: Shield,
      title: 'Full Refund Policy',
      description: 'If the post doesn\'t go live or meet guarantees, get your money back.'
    }
  ];

  const benefits = [
    'Reach millions of engaged followers',
    'Authentic influencer endorsements',
    'Multiple content formats (Posts, Stories, Reels, Videos)',
    'Detailed analytics and proof of posting',
    'Brand-safe verified accounts only',
    'Flexible budgets for all business sizes'
  ];

  const faqs = [
    {
      question: 'What social media platforms do you cover?',
      answer: 'We offer promotions on Instagram (feed posts, stories, reels), YouTube (videos, shorts, mentions), Telegram channels, and other major platforms. Each platform has verified accounts with real engagement.'
    },
    {
      question: 'How do you verify influencer accounts?',
      answer: 'We verify all accounts for authentic engagement, follower quality, and brand safety. We check engagement rates, follower demographics, and content history before adding any account to our network.'
    },
    {
      question: 'What content formats are available?',
      answer: 'Depending on the platform, we offer feed posts, stories, reels, video mentions, dedicated videos, shorts, and channel posts. Each listing clearly shows what format is included.'
    },
    {
      question: 'Do I get analytics after posting?',
      answer: 'Yes! You receive detailed analytics including views, likes, comments, and engagement metrics. We provide screenshots and links as proof of posting.'
    }
  ];

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'instagram': return <Instagram className="w-5 h-5" />;
      case 'youtube': return <Youtube className="w-5 h-5" />;
      case 'telegram': return <MessageCircle className="w-5 h-5" />;
      default: return <Users className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Social Media Marketing India | Influencer Promotions | BusinessMint PR</title>
        <meta name="title" content="Social Media Marketing India | Influencer Promotions | BusinessMint PR" />
        <meta name="description" content="Get your brand promoted by 100+ verified influencers on Instagram, YouTube, and Telegram. 10M+ total reach, guaranteed views, authentic engagement. Fast 24-48 hour turnaround." />
        <meta name="keywords" content="social media marketing India, influencer marketing, Instagram promotion, YouTube promotion, Telegram marketing, brand promotion, social media advertising, influencer collaboration" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="BusinessMint PR" />
        <link rel="canonical" href={`${SITE_URL}/services/social-media`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/services/social-media`} />
        <meta property="og:title" content="Social Media Marketing | Influencer Promotions" />
        <meta property="og:description" content="Get your brand promoted by verified influencers on Instagram, YouTube, and Telegram. Guaranteed views and authentic engagement." />
        <meta property="og:site_name" content="BusinessMint PR" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${SITE_URL}/services/social-media`} />
        <meta name="twitter:title" content="Social Media Marketing | BusinessMint PR" />
        <meta name="twitter:description" content="100+ verified influencers, 10M+ reach. Instagram, YouTube, Telegram promotions with guaranteed results." />
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(socialMediaSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0d0d0d] py-20 md:py-32">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-pink-500/20 to-red-500/20"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-manrope uppercase tracking-widest mb-6">
              <Instagram className="w-4 h-4" />
              Social Media Services
            </div>
            
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
              Amplify Your Brand on{' '}
              <span className="text-[#D4AF37]">Social Media</span>
            </h1>
            
            <p className="font-manrope text-lg md:text-xl text-gray-400 mb-10 max-w-2xl">
              Get your brand promoted by verified influencers and popular pages on Instagram, YouTube, and Telegram. Real engagement, guaranteed results.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/marketplace?type=social"
                className="btn-shimmer inline-flex items-center justify-center gap-3 px-10 py-5 font-manrope text-sm uppercase tracking-widest"
              >
                Browse Influencers
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#platforms"
                className="inline-flex items-center justify-center gap-3 px-10 py-5 border-2 border-[#D4AF37]/50 text-white font-manrope text-sm uppercase tracking-widest hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
              >
                View Platforms
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#D4AF37] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">100+</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Verified Influencers</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">10M+</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Total Reach</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">24h</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Avg Turnaround</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">100%</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Verified Accounts</div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section id="platforms" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Supported Platforms
            </h2>
            <p className="font-manrope text-[#737373] max-w-2xl mx-auto">
              Promote your brand across the most popular social media platforms
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {platforms.map((platform, index) => {
              const Icon = platform.icon;
              return (
                <div key={index} className="group relative bg-[#fafafa] border border-[#e5e5e5] p-8 hover:border-[#D4AF37] transition-all overflow-hidden">
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${platform.color} opacity-10 rounded-bl-full`}></div>
                  <div className="relative">
                    <div className={`w-16 h-16 bg-gradient-to-br ${platform.color} rounded-xl flex items-center justify-center mb-6 text-white`}>
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3 className="font-playfair text-2xl font-bold mb-3 text-[#1a1a1a]">{platform.name}</h3>
                    <p className="font-manrope text-sm text-[#737373] mb-6">{platform.description}</p>
                    <Link
                      to={`/marketplace?type=social&platform=${platform.name.toLowerCase()}`}
                      className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm font-semibold hover:gap-3 transition-all"
                    >
                      Browse {platform.name} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Why Choose Our Social Media Services?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white border border-[#e5e5e5] p-6 hover:border-[#D4AF37] transition-colors">
                  <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-playfair text-lg font-bold mb-2 text-[#1a1a1a]">{feature.title}</h3>
                  <p className="font-manrope text-sm text-[#737373]">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits & Sample Pages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6 text-[#1a1a1a]">
                Benefits of Social Media Promotion
              </h2>
              <p className="font-manrope text-[#737373] mb-8">
                Leverage the power of influencer marketing to reach your target audience authentically.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="font-manrope text-[#525252]">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/marketplace?type=social"
                className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest font-semibold hover:bg-[#B8960F] transition-colors"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="bg-[#0d0d0d] p-8 border border-[#D4AF37]/20">
              <h3 className="font-playfair text-xl font-bold text-white mb-6">Featured Influencers</h3>
              {loading ? (
                <div className="text-gray-500">Loading...</div>
              ) : (
                <div className="space-y-4">
                  {socialPages.map((page) => (
                    <Link
                      key={page.id}
                      to={`/social-page/${page.id}`}
                      className="flex items-center justify-between p-4 bg-[#1a1a1a] border border-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white">
                          {getPlatformIcon(page.platform)}
                        </div>
                        <div>
                          <div className="font-playfair font-bold text-white">{page.name}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            <Users className="w-3 h-3" />
                            {page.followers?.toLocaleString() || '10K+'} followers
                          </div>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-500" />
                    </Link>
                  ))}
                </div>
              )}
              <Link
                to="/marketplace?type=social"
                className="block w-full mt-6 py-3 text-center text-[#D4AF37] font-manrope text-sm uppercase tracking-widest hover:text-white transition-colors"
              >
                View All Influencers →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#f5f5f5]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-[#e5e5e5] bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#fafafa] transition-colors"
                >
                  <span className="font-manrope font-semibold text-[#1a1a1a] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#737373] flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="font-manrope text-sm text-[#737373]">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#D4AF37]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6 text-[#0d0d0d]">
            Ready to Go Viral?
          </h2>
          <p className="font-manrope text-[#0d0d0d]/80 mb-8 max-w-2xl mx-auto">
            Connect with verified influencers and amplify your brand's social presence today.
          </p>
          <Link
            to="/marketplace?type=social"
            className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-[#0d0d0d] text-white font-manrope text-sm uppercase tracking-widest hover:bg-black transition-all"
          >
            Browse Influencers
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SocialMedia;
